import { useState, useEffect, useRef } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";

/**
 * DateField — reemplazo de `<input type="date">` que muestra la fecha SIEMPRE
 * en formato colombiano `DD/MM/AAAA`, independiente del locale del navegador.
 *
 * - `value`: ISO YYYY-MM-DD (o vacío)
 * - `onChange`: recibe ISO YYYY-MM-DD (o "" cuando se limpia)
 * - Internamente usa un `<input type="date">` oculto para el date picker nativo,
 *   pero muestra un input de texto controlado con el valor formateado.
 *   Al hacer click en el ícono se abre el picker nativo (via showPicker cuando existe).
 *
 * Uso:
 *   <DateField value={fecha} onChange={setFecha} />
 */
export default function DateField({
  value = "",
  onChange,
  placeholder = "DD/MM/AAAA",
  className = "",
  disabled = false,
  "data-testid": testId,
  autoFocus,
}) {
  const [txt, setTxt] = useState(isoToDMY(value));
  const nativeRef = useRef(null);

  // Sincronizamos si el value externo cambia (p. ej. presets Hoy/Últimos 7 días).
  useEffect(() => { setTxt(isoToDMY(value)); }, [value]);

  const commit = (raw) => {
    const iso = dmyToIso(raw);
    // Si el texto quedó vacío mandamos limpieza.
    if (!raw) { onChange?.(""); return; }
    if (iso) onChange?.(iso);
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el || disabled) return;
    try {
      if (typeof el.showPicker === "function") el.showPicker();
      else el.click();
    } catch { /* algunos browsers bloquean showPicker si no hay gesto */ }
  };

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <input
        type="text"
        inputMode="numeric"
        pattern="\d{2}/\d{2}/\d{4}"
        placeholder={placeholder}
        value={txt}
        disabled={disabled}
        autoFocus={autoFocus}
        data-testid={testId}
        onChange={(e) => {
          const masked = maskDMY(e.target.value);
          setTxt(masked);
          if (masked.length === 10) commit(masked);
          else if (masked.length === 0) commit("");
        }}
        onBlur={() => commit(txt)}
        className="h-10 w-full rounded-md border border-input bg-transparent px-3 pr-10 text-sm tabular-nums font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      {/* Native picker oculto para no exponer el layout M/D/Y del sistema */}
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        className="absolute opacity-0 pointer-events-none w-0 h-0 -z-10"
      />
      <div className="absolute right-1.5 flex items-center gap-0.5">
        {value && !disabled && (
          <button
            type="button"
            onClick={() => { setTxt(""); onChange?.(""); }}
            className="h-6 w-6 flex items-center justify-center rounded hover:bg-muted text-muted-foreground/70"
            title="Limpiar fecha"
            aria-label="Limpiar fecha"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          className="h-6 w-6 flex items-center justify-center rounded hover:bg-primary/10 text-muted-foreground hover:text-primary disabled:opacity-40"
          title="Abrir calendario"
          aria-label="Abrir calendario"
        >
          <CalendarIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function isoToDMY(iso) {
  if (!iso || typeof iso !== "string" || iso.length < 10) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
}

function dmyToIso(dmy) {
  if (!dmy || dmy.length !== 10) return "";
  const parts = dmy.split("/");
  if (parts.length !== 3) return "";
  const [d, m, y] = parts;
  if (!/^\d{2}$/.test(d) || !/^\d{2}$/.test(m) || !/^\d{4}$/.test(y)) return "";
  const dd = parseInt(d, 10), mm = parseInt(m, 10), yy = parseInt(y, 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yy < 1900 || yy > 2100) return "";
  return `${y}-${m}-${d}`;
}

/**
 * Máscara progresiva: acepta solo dígitos, formatea DD/MM/AAAA a medida
 * que el usuario tipea. Devuelve un string ya con separadores.
 */
function maskDMY(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}
